import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Chip,
  useTheme,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Google as GoogleIcon,
  VideoCall as VideoCallIcon,
  CalendarToday as CalendarIcon,
  School as SchoolIcon,
  TableChart as TableChartIcon,
  ContentCopy as ContentCopyIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { ClassSession } from '../../../types/tutors.types';
import { apiService } from '../../../services/api';
import DayIndicators from './DayIndicators';

interface ClassSessionCardProps {
  session: ClassSession;
}

const ClassSessionCard: React.FC<ClassSessionCardProps> = ({ session }) => {
  const theme = useTheme();
  
  // State for Exam Link functionality
  const [examLinkLoading, setExamLinkLoading] = useState(false);
  const [examLinkModalOpen, setExamLinkModalOpen] = useState(false);
  const [modalContentLoading, setModalContentLoading] = useState(false);
  const [examLink, setExamLink] = useState<string | null>(null);
  const [reSitExamLink, setReSitExamLink] = useState<string | null>(null);
  const [examLinkError, setExamLinkError] = useState<string | null>(null);
  const [reSitExamLinkError, setReSitExamLinkError] = useState<string | null>(null);
  const [generalError, setGeneralError] = useState<string | null>(null);

  // Listen for global authentication events
  useEffect(() => {
    const handleAuthUnauthorized = () => {
      // If the modal is open and we get an auth error, close it and show auth message
      if (examLinkModalOpen) {
        setGeneralError('Authentication failed. Please log in again to access exam links.');
      }
    };

    window.addEventListener('auth:unauthorized', handleAuthUnauthorized);
    
    return () => {
      window.removeEventListener('auth:unauthorized', handleAuthUnauthorized);
    };
  }, [examLinkModalOpen]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleGoogleDriveClick = () => {
    window.open(session.googleDriveUrl, '_blank');
  };

  const handleJoinClassClick = () => {
    const joinUrl = `https://join.csttraining.online/${session.courseGuid}`;
    window.open(joinUrl, '_blank');
  };

  const handleCourseRegisterClick = () => {
    window.open(session.courseRegisterUrl, '_blank');
  };

  const handleCopyGuid = async () => {
    try {
      await navigator.clipboard.writeText(session.courseGuid);
      // You could add a toast notification here if you have a notification system
    } catch (err) {
      console.error('Failed to copy course GUID:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = session.courseGuid;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  const handleExamLinkClick = async () => {
    setExamLinkLoading(true);
    
    // Reset all state variables
    setExamLinkError(null);
    setReSitExamLinkError(null);
    setGeneralError(null);
    setExamLink(null);
    setReSitExamLink(null);

    try {
      const response = await apiService.getExamLink(session.courseGuid);
      
      // Process the response and update state
      if (response.success) {
        // Set the available links
        if (response.hasExamLink && response.examLink) {
          setExamLink(response.examLink);
        }
        if (response.hasReSitExamLink && response.reSitExamLink) {
          setReSitExamLink(response.reSitExamLink);
        }
        
        // Set individual error messages for missing links
        if (response.examLinkError) {
          setExamLinkError(response.examLinkError);
        }
        if (response.reSitExamLinkError) {
          setReSitExamLinkError(response.reSitExamLinkError);
        }
      } else {
        // General error when both links are missing or API fails
        setGeneralError(response.errorMessage || 'Failed to fetch exam links. Please contact Course Ops for assistance.');
      }
      
      // Wait a brief moment to ensure all state updates have completed
      await new Promise(resolve => setTimeout(resolve, 50));
      
    } catch (error) {
      console.error('Error fetching exam links:', error);
      setGeneralError('Failed to fetch exam links. Please contact Course Ops for assistance.');
      
      // Wait a brief moment to ensure state update has completed
      await new Promise(resolve => setTimeout(resolve, 50));
    } finally {
      setExamLinkLoading(false);
      // Only open modal after all processing is complete
      setExamLinkModalOpen(true);
    }
  };

  const handleCopyExamLink = async () => {
    if (!examLink) return;
    
    try {
      await navigator.clipboard.writeText(examLink);
      // You could add a toast notification here if you have a notification system
    } catch (err) {
      console.error('Failed to copy exam link:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = examLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  const handleCopyReSitExamLink = async () => {
    if (!reSitExamLink) return;
    
    try {
      await navigator.clipboard.writeText(reSitExamLink);
      // You could add a toast notification here if you have a notification system
    } catch (err) {
      console.error('Failed to copy re-sit exam link:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = reSitExamLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  const handleCloseExamLinkModal = () => {
    setExamLinkModalOpen(false);
    setModalContentLoading(false);
    setExamLink(null);
    setReSitExamLink(null);
    setExamLinkError(null);
    setReSitExamLinkError(null);
    setGeneralError(null);
  };

  const assignedDaysCount = session.assignedDates.length;
  const totalDaysCount = session.courseDates.length;

  return (
    <Card 
      sx={{ 
        width: '100%',
        mb: 2,
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        '&:hover': {
          boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
        },
        transition: 'box-shadow 0.2s ease-in-out',
        overflow: 'hidden',
      }}
    >
      {/* Course GUID Header */}
      <Box sx={{
        backgroundColor: theme.palette.grey[100],
        px: { xs: 2, sm: 3 },
        py: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: `1px solid ${theme.palette.grey[200]}`,
      }}>
        <Typography 
          variant="body2" 
          sx={{ 
            fontFamily: 'monospace',
            fontSize: { xs: '0.75rem', sm: '0.8rem' },
            color: theme.palette.text.secondary,
            fontWeight: 500,
          }}
        >
          Course ID: {session.courseGuid}
        </Typography>
        <Tooltip title="Copy Course ID" arrow>
          <Button
            size="small"
            onClick={handleCopyGuid}
            sx={{
              minWidth: 'auto',
              p: 0.5,
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: theme.palette.grey[200],
                color: theme.palette.text.primary,
              },
            }}
          >
            <ContentCopyIcon sx={{ fontSize: 16 }} />
          </Button>
        </Tooltip>
      </Box>

      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        {/* Mobile: Stack vertically, Desktop: Side by side */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', md: 'flex-start' }, 
          mb: 2,
          gap: { xs: 2, md: 0 }
        }}>
          {/* Event Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
            <SchoolIcon sx={{ color: theme.palette.primary.main, fontSize: { xs: 18, sm: 20 } }} />
            <Typography variant="h6" component="h3" sx={{ 
              fontWeight: 600,
              fontSize: { xs: '1.1rem', sm: '1.25rem' }
            }}>
              {session.eventTitle}
            </Typography>
          </Box>

          {/* Teaching Days */}
          <Box sx={{ 
            textAlign: { xs: 'left', md: 'right' },
            width: { xs: '100%', md: 'auto' }
          }}>
            <Typography variant="body2" color="text.secondary" sx={{ 
              mb: 1, 
              fontWeight: 500, 
              fontSize: { xs: '11px', sm: '12px' }
            }}>
              Teaching Days
            </Typography>
            <DayIndicators 
              courseDates={session.courseDates}
              assignedDates={session.assignedDates}
            />
          </Box>
        </Box>

        {/* Second Row: Start Date and Days Summary */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', sm: 'center' }, 
          mb: 3,
          gap: { xs: 1, sm: 0 }
        }}>
          {/* Start Date */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarIcon sx={{ color: theme.palette.text.secondary, fontSize: { xs: 14, sm: 16 } }} />
            <Typography variant="body2" color="text.secondary" sx={{
              fontSize: { xs: '0.8rem', sm: '0.875rem' }
            }}>
              Starts {formatDate(session.startDate)}
            </Typography>
          </Box>

          {/* Days Summary */}
          <Chip 
            label={`${assignedDaysCount} of ${totalDaysCount} days`}
            size="small"
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: 'white',
              fontWeight: 500,
              fontSize: { xs: '0.7rem', sm: '0.75rem' },
              height: { xs: 24, sm: 32 }
            }}
          />
        </Box>

        {/* Action Buttons */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1.5, sm: 2 }
        }}>
          <Tooltip 
            title={!session.hasGoogleDriveAccess ? "Contact IT support for assistance" : ""}
            arrow
            placement="top"
          >
            <span style={{ flex: 1 }}>
              <Button
                variant="outlined"
                startIcon={<GoogleIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />}
                onClick={handleGoogleDriveClick}
                disabled={!session.hasGoogleDriveAccess}
                sx={{
                  width: '100%',
                  borderColor: theme.palette.grey[300],
                  color: theme.palette.text.primary,
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  py: { xs: 1, sm: 1.5 },
                  '&:hover': {
                    borderColor: theme.palette.grey[400],
                    backgroundColor: theme.palette.grey[50],
                  },
                }}
              >
                Google Drive
              </Button>
            </span>
          </Tooltip>
          
          <Tooltip 
            title={!session.hasCourseRegisterAccess ? "Contact IT support for assistance" : ""}
            arrow
            placement="top"
          >
            <span style={{ flex: 1 }}>
              <Button
                variant="outlined"
                startIcon={<TableChartIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />}
                onClick={handleCourseRegisterClick}
                disabled={!session.hasCourseRegisterAccess}
                sx={{
                  width: '100%',
                  borderColor: theme.palette.grey[300],
                  color: theme.palette.text.primary,
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  py: { xs: 1, sm: 1.5 },
                  '&:hover': {
                    borderColor: theme.palette.grey[400],
                    backgroundColor: theme.palette.grey[50],
                  },
                }}
              >
                Course Register
              </Button>
            </span>
          </Tooltip>

          <span style={{ flex: 0.5 }}>
            <Button
              variant="outlined"
              startIcon={examLinkLoading ? <CircularProgress size={16} /> : <AssignmentIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />}
              onClick={handleExamLinkClick}
              disabled={examLinkLoading}
              sx={{
                width: '100%',
                borderColor: theme.palette.grey[300],
                color: theme.palette.text.primary,
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                py: { xs: 1, sm: 1.5 },
                '&:hover': {
                  borderColor: theme.palette.grey[400],
                  backgroundColor: theme.palette.grey[50],
                },
              }}
            >
              {examLinkLoading ? 'Loading...' : 'Exam Link'}
            </Button>
          </span>
          
          <Button
            variant="contained"
            startIcon={<VideoCallIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />}
            onClick={handleJoinClassClick}
            sx={{
              flex: 1,
              backgroundColor: theme.palette.primary.main,
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              py: { xs: 1, sm: 1.5 },
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },
            }}
          >
            Join Class
          </Button>
        </Box>
      </CardContent>

      {/* Exam Link Modal */}
      <Dialog
        open={examLinkModalOpen}
        onClose={handleCloseExamLinkModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 1,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            Exam Link
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ pb: 2 }}>
          {/* General error when both links are missing */}
          {generalError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {generalError}
            </Alert>
          )}

          {/* Regular Exam Link Section */}
          {examLink && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Exam Link
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Click the link below to access the exam, or use the copy button to copy the link.
              </Typography>
              <Box sx={{
                p: 2,
                backgroundColor: theme.palette.grey[50],
                borderRadius: 1,
                border: `1px solid ${theme.palette.grey[200]}`,
                mb: 2,
                wordBreak: 'break-all',
              }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                  }}
                >
                  {examLink}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<ContentCopyIcon />}
                  onClick={handleCopyExamLink}
                  sx={{ flex: 1 }}
                >
                  Copy Link
                </Button>
                <Button
                  variant="contained"
                  onClick={() => window.open(examLink, '_blank')}
                  sx={{ flex: 1 }}
                >
                  Open Exam
                </Button>
              </Box>
            </Box>
          )}

          {/* Exam Link Error */}
          {examLinkError && !generalError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Exam Link:</strong> {examLinkError}
              </Typography>
            </Alert>
          )}

          {/* Re-Sit Exam Link Section */}
          {reSitExamLink && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Re-Sit Exam Link
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Click the link below to access the re-sit exam, or use the copy button to copy the link.
              </Typography>
              <Box sx={{
                p: 2,
                backgroundColor: theme.palette.grey[50],
                borderRadius: 1,
                border: `1px solid ${theme.palette.grey[200]}`,
                mb: 2,
                wordBreak: 'break-all',
              }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                  }}
                >
                  {reSitExamLink}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<ContentCopyIcon />}
                  onClick={handleCopyReSitExamLink}
                  sx={{ flex: 1 }}
                >
                  Copy Link
                </Button>
                <Button
                  variant="contained"
                  onClick={() => window.open(reSitExamLink, '_blank')}
                  sx={{ flex: 1 }}
                >
                  Open Re-Sit Exam
                </Button>
              </Box>
            </Box>
          )}

          {/* Re-Sit Exam Link Error */}
          {reSitExamLinkError && !generalError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Re-Sit Exam Link:</strong> {reSitExamLinkError}
              </Typography>
            </Alert>
          )}

          {/* Show message when no content is available */}
          {!generalError && !examLink && !reSitExamLink && !examLinkError && !reSitExamLinkError && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No exam links available at this time.
            </Typography>
          )}
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseExamLinkModal} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default ClassSessionCard;
